import { Toast, useToast } from "@oysterhr/ui"
import { PropsWithChildren, useCallback, createContext, useContext } from "react"
import { useTranslation } from "react-i18next"
import { AgreementTypeEnum, EnvelopeAutomationStateEnum, EnvelopeTypeEnum } from "@oysterhr/schema"
import { useAgreementsEnvelopePolling } from "../hooks/useAgreementsEnvelopePolling"

type EnvelopePollingContextType = {
  startShareAgreementsPollingWithFeedback: () => void
  startSendReminderPollingWithFeedback: (agreementType: AgreementTypeEnum) => void
  startUpdateEnvelopePollingWithFeedback: () => void
  startOSAPollingWithFeedback: () => void
}

const EnvelopePollingContext = createContext<EnvelopePollingContextType | null>(null)

/**
 * This context should be used to share envelope polling mechanism between components
 * It should handle showing feedback messages to the user.
 * This is mainly used because we want to trigger the polling inside a modal,
 * but we want to show the feedback messages in the parent component.
 * Also, if we close the modal, the polling stops, and we don't want that.
 */
export function EnvelopePollingProvider({
  children,
  employmentId,
  isContractor = false,
}: PropsWithChildren<{ employmentId: string; isContractor?: boolean }>) {
  const [t] = useTranslation("Agreements")
  const toast = useToast()

  const [startPolling] = useAgreementsEnvelopePolling(employmentId, isContractor)

  const startShareAgreementsPollingWithFeedback = useCallback(() => {
    // Currying the startPolling function to avoid passing the data argument
    startPolling(data => {
      const hasFailedUpdate =
        data.employment?.hiring?.envelopes.find(
          env => env.envelopeType === EnvelopeTypeEnum.EmploymentAgreements,
        )?.automationState === EnvelopeAutomationStateEnum.SendFailed

      const translateKey = hasFailedUpdate
        ? "shareAgreementsCTA.flashMessage.error"
        : "shareAgreementsCTA.flashMessage.success"

      toast({
        render: (
          <Toast variation={hasFailedUpdate ? "error" : "success"}>
            <Toast.Content>{t(translateKey)}</Toast.Content>
          </Toast>
        ),
      })
    })
  }, [startPolling, t, toast])

  const startSendReminderPollingWithFeedback = useCallback(
    (agreementType: AgreementTypeEnum) => {
      startPolling(data => {
        const envelope = data.employment?.hiring?.envelopes.find(env => {
          if (agreementType === AgreementTypeEnum.OysterServiceAgreement) {
            return env.envelopeType === EnvelopeTypeEnum.ServiceAgreement
          }
          return env.envelopeType === EnvelopeTypeEnum.EmploymentAgreements
        })

        if (!envelope) return

        const hasFailedUpdate = [
          EnvelopeAutomationStateEnum.ResendFailed,
          EnvelopeAutomationStateEnum.SendFailed,
        ].includes(envelope.automationState)

        const translateKey = hasFailedUpdate
          ? "sendReminderModal.flashMessage.error"
          : "sendReminderModal.flashMessage.success"

        toast({
          render: (
            <Toast variation={hasFailedUpdate ? "error" : "success"}>
              <Toast.Content>
                <div data-testid="flash-message-success">{t(translateKey)}</div>
              </Toast.Content>
            </Toast>
          ),
        })
      })
    },
    [startPolling, t, toast],
  )

  const startUpdateEnvelopePollingWithFeedback = useCallback(() => {
    startPolling(data => {
      const hasFailedUpdate =
        data.employment?.hiring?.envelopes.find(
          env => env.envelopeType === EnvelopeTypeEnum.EmploymentAgreements,
        )?.automationState === EnvelopeAutomationStateEnum.UpdateFailed

      const translateKey = hasFailedUpdate
        ? "updateEnvelopeAlert.flashMessage.error"
        : "updateEnvelopeAlert.flashMessage.success"

      toast({
        render: (
          <Toast variation={hasFailedUpdate ? "error" : "success"}>
            <Toast.Content>{t(translateKey)}</Toast.Content>
          </Toast>
        ),
      })
    })
  }, [startPolling, t, toast])

  const startOSAPollingWithFeedback = useCallback(() => {
    startPolling(data => {
      const oysterServiceAgreementEnvelope = data.employment?.hiring?.envelopes.find(
        env => env.envelopeType === EnvelopeTypeEnum.ServiceAgreement,
      )
      const isESignEnabled = data.employment?.hiring?.agreements.find(
        agreement => agreement.agreementType === AgreementTypeEnum.OysterServiceAgreement,
      )?.isESignEnabled

      if (!oysterServiceAgreementEnvelope || !isESignEnabled) return null

      const hasFailedUpdate = [
        EnvelopeAutomationStateEnum.SendFailed,
        EnvelopeAutomationStateEnum.UpdateFailed,
      ].includes(oysterServiceAgreementEnvelope.automationState)

      const translateKey = hasFailedUpdate
        ? "verifyAgreementModal.oysterServiceAgreement.feedbackMessages.error"
        : "verifyAgreementModal.oysterServiceAgreement.feedbackMessages.success.content"

      toast({
        render: (
          <Toast variation={hasFailedUpdate ? "error" : "success"}>
            {!hasFailedUpdate && (
              <Toast.Title>
                {t("verifyAgreementModal.oysterServiceAgreement.feedbackMessages.success.title")}
              </Toast.Title>
            )}
            <Toast.Content>{t(translateKey)}</Toast.Content>
          </Toast>
        ),
      })
    })
  }, [startPolling, t, toast])

  return (
    <EnvelopePollingContext.Provider
      value={{
        startShareAgreementsPollingWithFeedback,
        startSendReminderPollingWithFeedback,
        startUpdateEnvelopePollingWithFeedback,
        startOSAPollingWithFeedback,
      }}
    >
      {children}
    </EnvelopePollingContext.Provider>
  )
}

export const useEnvelopePollingContext = () => {
  const context = useContext(EnvelopePollingContext)
  if (!context) {
    throw new Error("useEnvelopeContext must be used within a EnvelopePollingProvider")
  }
  return context
}
